import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { charactersAPI, elementsAPI, pillarsAPI } from '../services/api';
import { Character, Element, Pillar } from '../types';
import { getImageUrl, hasCustomImage } from '../utils/imageUtils';
import OptimizedImage from '../components/OptimizedImage';
import CreateCharacterModal from '../components/CreateCharacterModal';
import CreateElementModal from '../components/CreateElementModal';
import CreatePillarModal from '../components/CreatePillarModal';
import '../styles/Overview.css';

interface CardStats {
  total: number;
  withImages: number;
  recentlyCreated: number;
}

interface OverviewStats {
  characters: CardStats;
  elements: CardStats;
  pillars: CardStats;
}

const Overview: React.FC = () => {
  console.log('📊 Overview component rendering');
  
  const [stats, setStats] = useState<OverviewStats>({
    characters: { total: 0, withImages: 0, recentlyCreated: 0 },
    elements: { total: 0, withImages: 0, recentlyCreated: 0 },
    pillars: { total: 0, withImages: 0, recentlyCreated: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [recentCharacters, setRecentCharacters] = useState<Character[]>([]);
  const [recentElements, setRecentElements] = useState<Element[]>([]);
  const [recentPillars, setRecentPillars] = useState<Pillar[]>([]);
  
  // Modal states
  const [showCreateCharacterModal, setShowCreateCharacterModal] = useState(false);
  const [showCreateElementModal, setShowCreateElementModal] = useState(false);
  const [showCreatePillarModal, setShowCreatePillarModal] = useState(false);

  useEffect(() => {
    loadOverviewData();
  }, []);

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Overview: Loading overview data...');

      // Load all data in parallel
      const [characters, elements, pillars] = await Promise.all([
        charactersAPI.getAll(),
        elementsAPI.getAll(),
        pillarsAPI.getAll()
      ]);

      // Calculate stats
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const characterStats: CardStats = {
        total: characters.length,
        withImages: characters.filter(c => hasCustomImage(c, undefined, 'character')).length,
        recentlyCreated: characters.filter(c => new Date(c.createdAt) > oneWeekAgo).length
      };

      const elementStats: CardStats = {
        total: elements.length,
        withImages: elements.filter(e => hasCustomImage(e, undefined, 'element')).length,
        recentlyCreated: elements.filter(e => e.createdAt && new Date(e.createdAt) > oneWeekAgo).length
      };

      const pillarStats: CardStats = {
        total: pillars.length,
        withImages: pillars.filter(p => hasCustomImage(p, undefined, 'pillar')).length,
        recentlyCreated: pillars.filter(p => p.createdAt && new Date(p.createdAt) > oneWeekAgo).length
      };

      setStats({
        characters: characterStats,
        elements: elementStats,
        pillars: pillarStats
      });

      // Get recent items (last 5 of each type)
      setRecentCharacters(characters.slice(0, 5));
      setRecentElements(elements.slice(0, 5));
      setRecentPillars(pillars.slice(0, 5));

      console.log('✅ Overview: Data loaded successfully');
    } catch (error) {
      console.error('❌ Overview: Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleCharacterCreated = (character: Character) => {
    // Refresh the overview data
    loadOverviewData();
  };

  const handleElementCreated = (element: Element) => {
    // Refresh the overview data
    loadOverviewData();
  };

  const handlePillarCreated = (pillar: Pillar) => {
    // Refresh the overview data
    loadOverviewData();
  };

  if (loading) {
    return (
      <div className="overview">
        <div className="loading">
          <p>Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overview">
      {/* Header */}
      <div className="overview-header">
        <h1 className="overview-title">Aethermap Dashboard</h1>
        <p className="overview-subtitle">Your world-building overview</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-card--characters">
          <div className="stat-card__header">
            <h3>Characters</h3>
            <Link to="/characters" className="stat-card__link">
              View All →
            </Link>
          </div>
          <div className="stat-card__stats">
            <div className="stat-item">
              <span className="stat-number">{stats.characters.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.characters.withImages}</span>
              <span className="stat-label">With Images</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.characters.recentlyCreated}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-card--elements">
          <div className="stat-card__header">
            <h3>Elements</h3>
            <Link to="/elements" className="stat-card__link">
              View All →
            </Link>
          </div>
          <div className="stat-card__stats">
            <div className="stat-item">
              <span className="stat-number">{stats.elements.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.elements.withImages}</span>
              <span className="stat-label">With Images</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.elements.recentlyCreated}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>
        </div>

        <div className="stat-card stat-card--pillars">
          <div className="stat-card__header">
            <h3>Pillars</h3>
            <Link to="/pillars" className="stat-card__link">
              View All →
            </Link>
          </div>
          <div className="stat-card__stats">
            <div className="stat-item">
              <span className="stat-number">{stats.pillars.total}</span>
              <span className="stat-label">Total</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.pillars.withImages}</span>
              <span className="stat-label">With Images</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.pillars.recentlyCreated}</span>
              <span className="stat-label">This Week</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Items */}
      <div className="recent-section">
        <h2 className="recent-section__title">Recent Activity</h2>
        
        <div className="recent-grid">
          {/* Recent Characters */}
          <div className="recent-card">
            <div className="recent-card__header">
              <h3>Recent Characters</h3>
              <Link to="/characters" className="recent-card__link">
                View All →
              </Link>
            </div>
            <div className="recent-card__content">
              {recentCharacters.length > 0 ? (
                recentCharacters.map(character => (
                  <div key={character._id} className="recent-item">
                    <OptimizedImage 
                      src={getImageUrl(character)} 
                      alt={character.name}
                      className="recent-item__image"
                      onError={() => {
                        // Error handling is done by the OptimizedImage component
                      }}
                      withCardOverlay={false}
                      simpleFallback={true}
                    />
                    <div className="recent-item__info">
                      <h4 className="recent-item__title">{character.name}</h4>
                      <p className="recent-item__date">{formatDate(character.createdAt)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="recent-empty">No characters yet</p>
              )}
            </div>
          </div>

          {/* Recent Elements */}
          <div className="recent-card">
            <div className="recent-card__header">
              <h3>Recent Elements</h3>
              <Link to="/elements" className="recent-card__link">
                View All →
              </Link>
            </div>
            <div className="recent-card__content">
              {recentElements.length > 0 ? (
                recentElements.map(element => (
                  <div key={element._id} className="recent-item">
                    <OptimizedImage 
                      src={getImageUrl(element)} 
                      alt={element.name}
                      className="recent-item__image"
                      onError={() => {
                        // Error handling is done by the OptimizedImage component
                      }}
                      withCardOverlay={false}
                      simpleFallback={true}
                    />
                    <div className="recent-item__info">
                      <h4 className="recent-item__title">{element.name}</h4>
                      <p className="recent-item__date">{formatDate(element.createdAt)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="recent-empty">No elements yet</p>
              )}
            </div>
          </div>

          {/* Recent Pillars */}
          <div className="recent-card">
            <div className="recent-card__header">
              <h3>Recent Pillars</h3>
              <Link to="/pillars" className="recent-card__link">
                View All →
              </Link>
            </div>
            <div className="recent-card__content">
              {recentPillars.length > 0 ? (
                recentPillars.map(pillar => (
                  <div key={pillar._id} className="recent-item">
                    <OptimizedImage 
                      src={getImageUrl(pillar)} 
                      alt={pillar.name}
                      className="recent-item__image"
                      onError={() => {
                        // Error handling is done by the OptimizedImage component
                      }}
                      withCardOverlay={false}
                      simpleFallback={true}
                    />
                    <div className="recent-item__info">
                      <h4 className="recent-item__title">{pillar.name}</h4>
                      <p className="recent-item__date">{formatDate(pillar.createdAt)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="recent-empty">No pillars yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2 className="quick-actions__title">Quick Actions</h2>
        <div className="quick-actions__grid">
          <button 
            onClick={() => setShowCreateCharacterModal(true)}
            className="quick-action quick-action--characters"
          >
            <div className="quick-action__icon">👤</div>
            <h3>Create Character</h3>
            <p>Add a new character to your world</p>
          </button>
          <button 
            onClick={() => setShowCreateElementModal(true)}
            className="quick-action quick-action--elements"
          >
            <div className="quick-action__icon">⚔️</div>
            <h3>Create Element</h3>
            <p>Add weapons, items, or abilities</p>
          </button>
          <button 
            onClick={() => setShowCreatePillarModal(true)}
            className="quick-action quick-action--pillars"
          >
            <div className="quick-action__icon">🏛️</div>
            <h3>Create Pillar</h3>
            <p>Add locations and organizations</p>
          </button>
        </div>
      </div>

      {/* Create Modals */}
      {showCreateCharacterModal && (
        <CreateCharacterModal
          onClose={() => setShowCreateCharacterModal(false)}
          onCharacterCreated={handleCharacterCreated}
        />
      )}
      
      <CreateElementModal
        isOpen={showCreateElementModal}
        onClose={() => setShowCreateElementModal(false)}
        onElementCreated={handleElementCreated}
      />
      
      <CreatePillarModal
        isOpen={showCreatePillarModal}
        onClose={() => setShowCreatePillarModal(false)}
        onPillarCreated={handlePillarCreated}
      />
    </div>
  );
};

export default Overview; 